import { useCallback, useMemo, useState } from 'react';
import { Layout, Menu } from 'antd';
import { useLocation, useNavigate } from 'react-router';
import MainLogo from '../../assets/images/logo.png';
import LogoOnly from '../../assets/images/logo-only.svg';
import {
    ApartmentOutlined,
    FormOutlined,
    InsertRowAboveOutlined,
    LogoutOutlined,
    PlusCircleOutlined,
} from '@ant-design/icons';
import './template.css';
import useUser from '@hooks/useUser';
import { signOut } from '@supa/supabase-config';
import { Avatar, Box, Button, Divider, Stack, Typography, useTheme } from '@mui/material';
import VersionInfo from '@components/atoms/VersionInfo';
import { mainGray, secondaryGray } from '@modules/theme';
import { ItemType, MenuItemType } from 'antd/es/menu/interface';
import useModalControl from '@hooks/useModalControl';
import useTeams from '@hooks/useTeams';
import { TEAM_ROLE } from '@type/db-types';
import SettingsAccessibilityIcon from '@mui/icons-material/SettingsAccessibility';
import { GroupsOutlined } from '@mui/icons-material';
import TeamSelector, { MAX_TEAMS_IN_SIDE_BAR } from './TeamSelector';
import SpoofModal from './SpoofModal';
import useManagedEvents from '@hooks/useManagedEvents';

const PUBLIC = 'public';

const SideBar = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { role, teams } = useTeams();
    const { athletes } = useUser();
    const { setShowTeamModal, setShowAddChildModal } = useModalControl();
    const { managedEvents } = useManagedEvents();

    const { Sider } = Layout;
    const collapsed = false;
    // const [collapsed, setCollapsed] = useState(false);
    const [current, setCurrent] = useState(location.pathname.split('/')[1]);

    const menuItemAuthorization = useCallback(
        (
            label: string,
            key: string,
            icon: null | JSX.Element,
            roles: TEAM_ROLE[] | typeof PUBLIC, // null is for everyone
            children: ItemType<MenuItemType>[] | undefined,
            type?: 'group' | 'item' | undefined,
        ): ItemType<MenuItemType> => {
            if (roles === PUBLIC || roles.includes(role as TEAM_ROLE)) {
                const item: ItemType<MenuItemType> = {
                    key,
                    icon,
                    children: children as ItemType<MenuItemType>[],
                    label,
                    type,
                };
                return item;
            }
            return null;
        },
        [role],
    );

    const addWrestler: ItemType<MenuItemType> = {
        style: { color: theme.palette.primary.main },
        // FIXME: when key is blank, it redirects to Dashboard every time
        // - this is a hacky fix to prevent a redirect, but after closing,
        // your current route Sidebar button is no longer highlighted
        key: window.location.pathname.slice(1) + `?${Date.now()}`,
        icon: <PlusCircleOutlined />,
        onClick: () => setShowAddChildModal(true),
        label: 'Add Wrestler',
        type: 'item',
    };
    // Add team
    const addTeam: ItemType<MenuItemType> = {
        style: { color: theme.palette.primary.main },
        // FIXME: when key is blank, it redirects to Dashboard every time
        // - this is a hacky fix to prevent a redirect, but after closing,
        // your current route Sidebar button is no longer highlighted
        key: window.location.pathname.slice(1) + `?${Date.now() + 1}`,
        icon: <PlusCircleOutlined />,
        onClick: () => setShowTeamModal(true),
        label: 'Add Team',
        type: 'item',
    };

    const wrestlersMenuItems = [
        menuItemAuthorization(
            'Wrestlers',
            'top-menu',
            null,
            PUBLIC,
            [
                // menuItemAuthorization(
                //     'Dashboard',
                //     'dashboard',
                //     <ProfileOutlined />,
                //     PUBLIC,
                //     undefined,
                // ),
                ...athletes.map((athlete) =>
                    menuItemAuthorization(
                        `${athlete.firstName} ${athlete.lastName}`,
                        `athletes/${athlete.athleteId}`,
                        <SettingsAccessibilityIcon />,
                        PUBLIC,
                        undefined,
                    ),
                ),
                addWrestler,
            ],
            'group',
        ),
    ];

    const teamMenuItems = [
        ...(teams?.length && teams?.length <= MAX_TEAMS_IN_SIDE_BAR
            ? teams.map((t) =>
                  menuItemAuthorization(
                      t.name,
                      `teams/${t.teamId}?tab=wrestlers`,
                      <GroupsOutlined />,
                      PUBLIC, // for now make them public so a user can see all teams they are a part of
                      undefined,
                  ),
              )
            : []),
        ...(teams?.length ? [] : [addTeam]), // only show add team if user has no teams
    ];

    const eventMenuItems = useMemo(
        () => [
            menuItemAuthorization(
                'Events',
                'OPERATION',
                null,
                PUBLIC,
                [
                    menuItemAuthorization(
                        'Public Events',
                        'events',
                        <InsertRowAboveOutlined />,
                        PUBLIC,
                        undefined,
                    ),
                    ...(managedEvents?.length
                        ? [
                              // only show manage events if user has events to manage
                              menuItemAuthorization(
                                  'Manage Events',
                                  'manage-events',
                                  <FormOutlined />,
                                  PUBLIC,
                                  undefined,
                              ),
                          ]
                        : []),
                    menuItemAuthorization(
                        'My Registered Events',
                        'registered-events',
                        <ApartmentOutlined />,
                        PUBLIC,
                        undefined,
                    ),
                ],
                'group',
            ),
        ],
        [managedEvents?.length, menuItemAuthorization],
    );

    const onClick = (e: { key: string }) => {
        setCurrent(e.key);
        navigate(`/${e.key}`);
    };

    return (
        <div className="sideb-main-wrapper">
            <Sider
                width={260}
                style={{
                    background: '#151929',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                }}
                collapsed={collapsed}
            >
                <Stack
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                    height="100vh"
                >
                    <Stack>
                        <div className={`side-bar-logo-wrapper  ${collapsed ? 'p-0' : ''}`}>
                            {collapsed ? (
                                <span className="only-logo">
                                    <LogoOnly />
                                </span>
                            ) : (
                                <span className="full-logo">
                                    <img src={MainLogo} />
                                </span>
                            )}
                        </div>
                        <Menu
                            mode="inline"
                            onClick={onClick}
                            selectedKeys={[current]}
                            style={{ height: '100%', borderRight: 0 }}
                            items={wrestlersMenuItems}
                            className="side-bar-menu"
                        />

                        <Menu
                            mode="inline"
                            onClick={onClick}
                            selectedKeys={[current]}
                            style={{ height: '100%', borderRight: 0 }}
                            items={[
                                menuItemAuthorization(
                                    `My Team${teams?.length && teams?.length > 1 ? 's' : ''}`,
                                    'teams-menu',
                                    null,
                                    PUBLIC,
                                    [],
                                    'group',
                                ),
                            ]}
                            className="side-bar-menu"
                        />
                        {/* Automatically appears when user has more than 5 teams */}
                        <TeamSelector />

                        <Menu
                            mode="inline"
                            onClick={onClick}
                            selectedKeys={[current]}
                            style={{ height: '100%', borderRight: 0 }}
                            items={teamMenuItems}
                            className="side-bar-menu"
                        />
                        <Menu
                            mode="inline"
                            onClick={onClick}
                            selectedKeys={[current]}
                            style={{ height: '100%', borderRight: 0 }}
                            items={eventMenuItems}
                            className="side-bar-menu"
                        />
                    </Stack>
                    <Footer collapsed={collapsed} />
                </Stack>
            </Sider>
        </div>
    );
};

export default SideBar;

function Footer({ collapsed }: { collapsed: boolean }) {
    const [showSpoofModal, setShowSpoofModal] = useState(false);
    const { user } = useUser();

    let userName = user?.firstName || '';
    const avatarAlt = ((user?.firstName?.[0] || '') + user?.lastName?.[0] || '').toUpperCase();
    if (user && user?.lastName) {
        userName = `${user?.firstName} ${user?.lastName}`;
    }

    return (
        <Stack>
            <VersionInfo small />
            <Box
                onClick={() => user && user?.email === 'team@bout.pro' && setShowSpoofModal(true)}
                display="flex"
                justifyContent="center"
                paddingTop={2}
            >
                <Avatar
                    alt={avatarAlt}
                    src={user?.imageUrl || undefined}
                    sx={{ background: ({ palette }) => palette.primary.dark, mr: 1.5 }}
                >
                    {avatarAlt}
                </Avatar>
                {collapsed ? null : (
                    <Stack>
                        <Typography variant="body2" sx={{ color: 'white' }}>
                            {userName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: mainGray }}>
                            {user?.email || ''}
                        </Typography>
                    </Stack>
                )}
            </Box>
            <Divider sx={{ margin: '1rem', borderColor: secondaryGray }} />
            <Button sx={{ marginBottom: '1rem' }} onClick={signOut} startIcon={<LogoutOutlined />}>
                Logout
            </Button>
            <SpoofModal showModal={showSpoofModal} onClose={() => setShowSpoofModal(false)} />
        </Stack>
    );
}
